<template>
    <TailwindLayout title="Onboarded Landlords">
        <div class="space-y-6">
            <BridgeBackLink v-if="$page.props.auth?.user?.is_admin" />

            <!-- Flash -->
            <div
                v-if="$page.props.flash?.success"
                class="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
            >
                {{ $page.props.flash.success }}
            </div>
            <div
                v-if="$page.props.flash?.error"
                class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
                {{ $page.props.flash.error }}
            </div>

            <!-- Hero -->
            <div class="relative overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-white px-6 py-7 sm:px-8">
                <div class="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-indigo-100/60 blur-2xl" />
                <div class="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                    <div class="flex items-start gap-4">
                        <span class="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-sm">
                            <FontAwesomeIcon :icon="faUsers" class="h-7 w-7" />
                        </span>
                        <div>
                            <h1 class="text-3xl font-bold tracking-tight text-rent-risk-blue-400">Onboarded Landlords</h1>
                            <p class="mt-1 max-w-2xl text-sm text-gray-600">
                                Plaid originator accounts eligible for rent payments. Enable self-service resubmission only after Plaid support confirms a denial is remediable.
                            </p>
                        </div>
                    </div>
                    <div class="shrink-0 rounded-xl border border-indigo-100 bg-white/80 px-4 py-3 text-sm shadow-sm backdrop-blur-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Showing</p>
                        <p class="mt-0.5 font-semibold text-gray-900">
                            {{ filteredUsers.length }} of {{ users.length }} landlord{{ users.length === 1 ? '' : 's' }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Stats -->
            <div class="grid grid-cols-2 gap-4 lg:grid-cols-5">
                <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Total onboarded</p>
                    <p class="mt-1 text-2xl font-bold text-gray-900">{{ stats.total }}</p>
                </div>
                <div class="rounded-xl border border-red-100 bg-red-50 p-4 shadow-sm">
                    <p class="text-xs font-semibold uppercase tracking-wider text-red-400">Denied</p>
                    <p class="mt-1 text-2xl font-bold text-red-700">{{ stats.denied }}</p>
                </div>
                <div class="rounded-xl border border-green-100 bg-green-50 p-4 shadow-sm">
                    <p class="text-xs font-semibold uppercase tracking-wider text-green-600">Resubmission on</p>
                    <p class="mt-1 text-2xl font-bold text-green-700">{{ stats.resubmissionEnabled }}</p>
                </div>
                <div class="rounded-xl border border-amber-100 bg-amber-50 p-4 shadow-sm">
                    <p class="text-xs font-semibold uppercase tracking-wider text-amber-600">Needs info</p>
                    <p class="mt-1 text-2xl font-bold text-amber-700">{{ stats.needsInfo }}</p>
                </div>
                <div class="rounded-xl border border-purple-100 bg-purple-50 p-4 shadow-sm">
                    <p class="text-xs font-semibold uppercase tracking-wider text-purple-600">Awaiting resubmit</p>
                    <p class="mt-1 text-2xl font-bold text-purple-700">{{ stats.awaitingResubmission }}</p>
                </div>
            </div>

            <!-- Workflow note -->
            <div class="flex gap-3 rounded-xl border border-rent-risk-blue-100 bg-rent-risk-blue-50/60 px-4 py-3 text-sm text-gray-700">
                <FontAwesomeIcon :icon="faCircleInfo" class="mt-0.5 h-4 w-4 shrink-0 text-rent-risk-blue-400" />
                <p>
                    <span class="font-semibold text-gray-900">Two options for denied landlords:</span>
                    enable one-shot resubmission when Plaid accepts a single requirement update, or reset onboarding when Plaid requires a full resubmission.
                </p>
            </div>

            <!-- Search + filters -->
            <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div class="relative max-w-xl flex-1">
                    <label for="landlord-search" class="sr-only">Search landlords</label>
                    <FontAwesomeIcon
                        :icon="faMagnifyingGlass"
                        class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    />
                    <input
                        id="landlord-search"
                        v-model="search"
                        type="search"
                        placeholder="Search by name or email…"
                        class="block w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm shadow-sm placeholder:text-gray-400 focus:border-rent-risk-blue-400 focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400/20"
                    />
                </div>
                <div class="flex flex-wrap gap-2">
                    <button
                        v-for="option in filterOptions"
                        :key="option.value"
                        type="button"
                        class="rounded-lg border px-3 py-1.5 text-sm font-medium transition"
                        :class="activeFilter === option.value
                            ? 'border-rent-risk-blue-400 bg-rent-risk-blue-400 text-white shadow-sm'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'"
                        @click="activeFilter = option.value"
                    >
                        {{ option.label }}
                        <span
                            class="ml-1.5 rounded-full px-1.5 py-0.5 text-xs"
                            :class="activeFilter === option.value ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'"
                        >
                            {{ option.count }}
                        </span>
                    </button>
                </div>
            </div>

            <!-- Desktop table -->
            <div class="hidden overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm lg:block">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50/80">
                            <tr>
                                <th scope="col" class="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    Landlord
                                </th>
                                <th scope="col" class="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    Plaid status
                                </th>
                                <th scope="col" class="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    Resubmission
                                </th>
                                <th scope="col" class="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100">
                            <tr
                                v-for="user in filteredUsers"
                                :key="user.id"
                                class="transition hover:bg-gray-50/80"
                                :class="user.plaid_resubmission_enabled ? 'bg-green-50/30' : ''"
                            >
                                <td class="px-5 py-4">
                                    <div class="flex items-center gap-3">
                                        <span
                                            class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-semibold text-white shadow-sm"
                                            :class="avatarClass(user.name)"
                                        >
                                            {{ initials(user.name) }}
                                        </span>
                                        <div class="min-w-0">
                                            <p class="truncate font-medium text-gray-900">{{ user.name }}</p>
                                            <p class="truncate text-sm text-gray-500">{{ user.email }}</p>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-5 py-4">
                                    <span
                                        class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize"
                                        :class="plaidStatusBadge(user)"
                                    >
                                        {{ formatPlaidStatus(user) }}
                                    </span>
                                </td>
                                <td class="px-5 py-4">
                                    <span
                                        class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                                        :class="user.plaid_resubmission_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'"
                                    >
                                        <span
                                            class="h-1.5 w-1.5 rounded-full"
                                            :class="user.plaid_resubmission_enabled ? 'bg-green-500' : 'bg-gray-400'"
                                        />
                                        {{ user.plaid_resubmission_enabled ? 'Enabled' : 'Disabled' }}
                                    </span>
                                </td>
                                <td class="px-5 py-4 text-right">
                                    <div v-if="showResubmissionAction(user) || showResetAction(user)" class="flex flex-wrap justify-end gap-2">
                                        <button
                                            v-if="showResubmissionAction(user)"
                                            type="button"
                                            class="inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition"
                                            :class="user.plaid_resubmission_enabled
                                                ? 'border border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                                                : 'bg-rent-risk-blue-400 text-white hover:opacity-90'"
                                            :title="resubmissionButtonTitle(user)"
                                            @click="openConfirmModal(user, 'resubmission')"
                                        >
                                            <FontAwesomeIcon :icon="user.plaid_resubmission_enabled ? faToggleOff : faToggleOn" class="h-4 w-4" />
                                            {{ user.plaid_resubmission_enabled ? 'Disable' : 'Enable' }}
                                        </button>
                                        <button
                                            v-if="showResetAction(user)"
                                            type="button"
                                            class="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2 text-sm font-semibold text-amber-800 transition hover:bg-amber-100"
                                            :title="resetButtonTitle"
                                            @click="openConfirmModal(user, 'reset')"
                                        >
                                            <FontAwesomeIcon :icon="faRotateLeft" class="h-4 w-4" />
                                            Reset onboarding
                                        </button>
                                    </div>
                                    <span v-else-if="user.awaiting_onboarding_resubmission" class="text-sm text-purple-700">
                                        Waiting for landlord (Billing page)
                                    </span>
                                    <span v-else class="text-sm text-gray-400">—</span>
                                </td>
                            </tr>
                            <tr v-if="!filteredUsers.length">
                                <td colspan="4" class="px-5 py-16 text-center">
                                    <div class="mx-auto flex max-w-sm flex-col items-center">
                                        <span class="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
                                            <FontAwesomeIcon :icon="faUsers" class="h-7 w-7" />
                                        </span>
                                        <p class="mt-4 text-sm font-medium text-gray-900">
                                            {{ users.length ? 'No landlords match your filters' : 'No onboarded landlords yet' }}
                                        </p>
                                        <p class="mt-1 text-sm text-gray-500">
                                            {{ users.length ? 'Try clearing search or choosing a different filter.' : 'Landlords appear here once they complete Plaid originator onboarding.' }}
                                        </p>
                                        <button
                                            v-if="search || activeFilter !== 'all'"
                                            type="button"
                                            class="mt-4 text-sm font-medium text-rent-risk-blue-400 hover:underline"
                                            @click="clearFilters"
                                        >
                                            Clear filters
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Mobile cards -->
            <div class="space-y-3 lg:hidden">
                <div
                    v-for="user in filteredUsers"
                    :key="`mobile-${user.id}`"
                    class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
                    :class="user.plaid_resubmission_enabled ? 'border-green-200 ring-1 ring-green-100' : ''"
                >
                    <div class="flex items-start gap-3">
                        <span
                            class="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-semibold text-white shadow-sm"
                            :class="avatarClass(user.name)"
                        >
                            {{ initials(user.name) }}
                        </span>
                        <div class="min-w-0 flex-1">
                            <p class="truncate font-semibold text-gray-900">{{ user.name }}</p>
                            <p class="truncate text-sm text-gray-500">{{ user.email }}</p>
                            <div class="mt-3 flex flex-wrap gap-2">
                                <span
                                    class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize"
                                    :class="plaidStatusBadge(user)"
                                >
                                    {{ formatPlaidStatus(user) }}
                                </span>
                                <span
                                    class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                                    :class="user.plaid_resubmission_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'"
                                >
                                    <span
                                        class="h-1.5 w-1.5 rounded-full"
                                        :class="user.plaid_resubmission_enabled ? 'bg-green-500' : 'bg-gray-400'"
                                    />
                                    Resubmission {{ user.plaid_resubmission_enabled ? 'on' : 'off' }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-if="showResubmissionAction(user) || showResetAction(user)" class="mt-4 flex flex-col gap-2 sm:flex-row">
                        <button
                            v-if="showResubmissionAction(user)"
                            type="button"
                            class="flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition"
                            :class="user.plaid_resubmission_enabled
                                ? 'border border-red-200 bg-red-50 text-red-700'
                                : 'bg-rent-risk-blue-400 text-white'"
                            :title="resubmissionButtonTitle(user)"
                            @click="openConfirmModal(user, 'resubmission')"
                        >
                            <FontAwesomeIcon :icon="user.plaid_resubmission_enabled ? faToggleOff : faToggleOn" class="h-4 w-4" />
                            {{ user.plaid_resubmission_enabled ? 'Disable resubmission' : 'Enable resubmission' }}
                        </button>
                        <button
                            v-if="showResetAction(user)"
                            type="button"
                            class="flex flex-1 items-center justify-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-800"
                            :title="resetButtonTitle"
                            @click="openConfirmModal(user, 'reset')"
                        >
                            <FontAwesomeIcon :icon="faRotateLeft" class="h-4 w-4" />
                            Reset onboarding
                        </button>
                    </div>
                    <p v-else-if="user.awaiting_onboarding_resubmission" class="mt-4 text-center text-sm font-medium text-purple-700">
                        Waiting for landlord to resubmit from the Billing page
                    </p>
                </div>

                <div
                    v-if="!filteredUsers.length"
                    class="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-12 text-center"
                >
                    <span class="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 text-gray-400">
                        <FontAwesomeIcon :icon="faUsers" class="h-6 w-6" />
                    </span>
                    <p class="mt-3 text-sm font-medium text-gray-900">
                        {{ users.length ? 'No landlords match your filters' : 'No onboarded landlords yet' }}
                    </p>
                    <button
                        v-if="search || activeFilter !== 'all'"
                        type="button"
                        class="mt-3 text-sm font-medium text-rent-risk-blue-400 hover:underline"
                        @click="clearFilters"
                    >
                        Clear filters
                    </button>
                </div>
            </div>
        </div>

        <ConfirmationModal
            :show="showConfirmModal"
            :title="confirmTitle"
            :message="confirmMessage"
            :confirm-button-text="confirmButtonText"
            :is-loading="isConfirmLoading"
            :icon-class="confirmIconClass"
            :icon-bg-class="confirmIconBgClass"
            :confirm-button-class="confirmButtonClass"
            @close="closeConfirmModal"
            @confirm="confirmAction"
        >
            <div v-if="confirmActionType === 'reset'" class="mt-4 space-y-3 text-left text-sm text-gray-600">
                <p>
                    This clears Plaid onboarding data so the landlord can submit the full form again from the Billing page.
                </p>
                <p>
                    If the landlord submits the same exact information again (individual: same first and last name; business: same business name), Plaid will most likely return
                    <span class="font-semibold text-gray-800">"application_name must be globally unique"</span>.
                    Only proceed if you know what you are doing.
                </p>
                <div class="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2.5">
                    <p class="font-semibold text-amber-900">application_name format</p>
                    <p class="mt-1 text-amber-900">
                        Individual: <span class="font-mono text-xs">INDIVIDUAL - First Last - RR - ST</span>
                    </p>
                    <p class="mt-1 text-amber-900">
                        Example: <span class="font-mono text-xs">INDIVIDUAL - John Smith - RR - CA</span>
                    </p>
                    <p class="mt-2 text-amber-900">
                        Business: <span class="font-mono text-xs">BUSINESS - Business Name - RR - ST</span>
                    </p>
                    <p class="mt-1 text-amber-900">
                        Example: <span class="font-mono text-xs">BUSINESS - Smith Properties LLC - RR - TX</span>
                    </p>
                </div>
            </div>
        </ConfirmationModal>
    </TailwindLayout>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
    faCircleInfo,
    faMagnifyingGlass,
    faRotateLeft,
    faToggleOff,
    faToggleOn,
    faUsers,
} from '@fortawesome/pro-light-svg-icons'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import BridgeBackLink from '@/Shared/BridgeBackLink.vue'
import ConfirmationModal from '@/Shared/ConfirmationModal.vue'

const AVATAR_PALETTE = [
    'bg-indigo-600',
    'bg-violet-600',
    'bg-sky-600',
    'bg-teal-600',
    'bg-rose-600',
    'bg-amber-600',
]

export default {
    components: {
        TailwindLayout,
        BridgeBackLink,
        ConfirmationModal,
        FontAwesomeIcon,
    },
    props: {
        users: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            faUsers,
            faMagnifyingGlass,
            faCircleInfo,
            faRotateLeft,
            faToggleOn,
            faToggleOff,
            search: '',
            activeFilter: 'all',
            showConfirmModal: false,
            confirmActionType: 'resubmission',
            selectedUser: null,
            isConfirmLoading: false,
        }
    },
    computed: {
        stats() {
            return {
                total: this.users.length,
                denied: this.users.filter((user) => user.transfer_diligence_status === 'denied').length,
                resubmissionEnabled: this.users.filter((user) => user.plaid_resubmission_enabled).length,
                needsInfo: this.users.filter((user) => user.transfer_diligence_status === 'more_information_required').length,
                awaitingResubmission: this.users.filter((user) => user.awaiting_onboarding_resubmission).length,
            }
        },
        filterOptions() {
            return [
                { value: 'all', label: 'All', count: this.users.length },
                { value: 'denied', label: 'Denied', count: this.stats.denied },
                { value: 'resubmission_enabled', label: 'Resubmission on', count: this.stats.resubmissionEnabled },
                { value: 'more_information_required', label: 'Needs info', count: this.stats.needsInfo },
                { value: 'awaiting_resubmission', label: 'Awaiting resubmit', count: this.stats.awaitingResubmission },
                { value: 'approved', label: 'Approved', count: this.users.filter((user) => user.transfer_diligence_status === 'approved').length },
            ]
        },
        filteredUsers() {
            const query = this.search.trim().toLowerCase()

            return this.users.filter((user) => {
                const matchesSearch = !query
                    || user.name?.toLowerCase().includes(query)
                    || user.email?.toLowerCase().includes(query)

                if (!matchesSearch) {
                    return false
                }

                if (this.activeFilter === 'all') {
                    return true
                }

                if (this.activeFilter === 'resubmission_enabled') {
                    return user.plaid_resubmission_enabled
                }

                if (this.activeFilter === 'awaiting_resubmission') {
                    return user.awaiting_onboarding_resubmission
                }

                return user.transfer_diligence_status === this.activeFilter
            })
        },
        resetButtonTitle() {
            return 'Start over from scratch: deletes the denied Plaid application (a snapshot is kept for auditing) so the landlord can submit a brand-new onboarding from their Payment Methods page.'
        },
        confirmTitle() {
            if (!this.selectedUser) {
                return ''
            }

            if (this.confirmActionType === 'reset') {
                return 'Reset Plaid onboarding?'
            }

            return this.selectedUser.plaid_resubmission_enabled
                ? 'Disable resubmission?'
                : 'Enable resubmission?'
        },
        confirmMessage() {
            if (!this.selectedUser) {
                return ''
            }

            if (this.confirmActionType === 'reset') {
                return `Reset onboarding for ${this.selectedUser.name}?`
            }

            return this.selectedUser.plaid_resubmission_enabled
                ? `${this.selectedUser.name} will no longer see the self-service resubmission form on their Payment Methods page.`
                : `${this.selectedUser.name} will be able to submit one document or field via their Payment Methods page. Confirm only after Plaid support has approved this resubmission.`
        },
        confirmButtonText() {
            if (!this.selectedUser) {
                return 'Confirm'
            }

            if (this.confirmActionType === 'reset') {
                return 'Reset onboarding'
            }

            return this.selectedUser.plaid_resubmission_enabled ? 'Disable' : 'Enable'
        },
        confirmIconClass() {
            if (!this.selectedUser) {
                return 'fa-solid fa-file-arrow-up h-6 w-6 text-rent-risk-blue-400'
            }

            if (this.confirmActionType === 'reset') {
                return 'fa-solid fa-rotate-left h-6 w-6 text-amber-600'
            }

            return this.selectedUser.plaid_resubmission_enabled
                ? 'fa-solid fa-toggle-off h-6 w-6 text-red-500'
                : 'fa-solid fa-toggle-on h-6 w-6 text-rent-risk-blue-400'
        },
        confirmIconBgClass() {
            if (!this.selectedUser) {
                return 'bg-blue-100'
            }

            if (this.confirmActionType === 'reset') {
                return 'bg-amber-100'
            }

            return this.selectedUser.plaid_resubmission_enabled ? 'bg-red-100' : 'bg-blue-100'
        },
        confirmButtonClass() {
            if (this.confirmActionType === 'reset') {
                return 'dangerButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 focus:outline-2 focus:outline-offset-2 focus:outline-transparent'
            }

            if (!this.selectedUser?.plaid_resubmission_enabled) {
                return 'primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent'
            }

            return 'dangerButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 focus:outline-2 focus:outline-offset-2 focus:outline-transparent'
        },
    },
    methods: {
        initials(name) {
            if (!name) {
                return '?'
            }

            return name
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part[0]?.toUpperCase() ?? '')
                .join('') || '?'
        },
        avatarClass(name) {
            const seed = (name || '').split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)

            return AVATAR_PALETTE[seed % AVATAR_PALETTE.length]
        },
        formatPlaidStatus(user) {
            if (user.awaiting_onboarding_resubmission) {
                return 'Awaiting resubmission'
            }

            if (!user.transfer_diligence_status) {
                return 'Unknown'
            }

            return user.transfer_diligence_status.replace(/_/g, ' ')
        },
        plaidStatusBadge(user) {
            if (user.awaiting_onboarding_resubmission) {
                return 'bg-purple-100 text-purple-700'
            }

            const status = user.transfer_diligence_status
            const badges = {
                approved: 'bg-green-100 text-green-700',
                denied: 'bg-red-100 text-red-700',
                rejected: 'bg-red-100 text-red-700',
                more_information_required: 'bg-amber-100 text-amber-800',
                pending: 'bg-blue-100 text-blue-700',
                under_review: 'bg-indigo-100 text-indigo-700',
            }

            return badges[status] || 'bg-gray-100 text-gray-700'
        },
        clearFilters() {
            this.search = ''
            this.activeFilter = 'all'
        },
        showResubmissionAction(user) {
            if (user.awaiting_onboarding_resubmission) {
                return false
            }

            if (user.plaid_resubmission_enabled) {
                return true
            }

            return user.transfer_diligence_status === 'denied'
        },
        resubmissionButtonTitle(user) {
            return user.plaid_resubmission_enabled
                ? 'Turn off self-service resubmission. The landlord will no longer see the resubmission form on their Payment Methods page.'
                : 'Let this landlord fix their denied onboarding themselves: they can submit one corrected document or field to Plaid from their Payment Methods page. Their existing Plaid application is kept.'
        },
        showResetAction(user) {
            return user.transfer_diligence_status === 'denied'
                && ! user.awaiting_onboarding_resubmission
        },
        openConfirmModal(user, actionType) {
            this.selectedUser = user
            this.confirmActionType = actionType
            this.showConfirmModal = true
        },
        closeConfirmModal() {
            this.showConfirmModal = false
            this.selectedUser = null
            this.confirmActionType = 'resubmission'
            this.isConfirmLoading = false
        },
        confirmAction() {
            if (!this.selectedUser) {
                return
            }

            this.isConfirmLoading = true

            if (this.confirmActionType === 'reset') {
                this.$inertia.post(
                    route('onboarded-users.reset-onboarding', this.selectedUser.id),
                    {},
                    {
                        preserveScroll: true,
                        onFinish: () => {
                            this.closeConfirmModal()
                        },
                    }
                )

                return
            }

            this.$inertia.post(
                route('onboarded-users.resubmission-flag', this.selectedUser.id),
                { enabled: !this.selectedUser.plaid_resubmission_enabled },
                {
                    preserveScroll: true,
                    onFinish: () => {
                        this.closeConfirmModal()
                    },
                }
            )
        },
    },
}
</script>
